import { useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  caretAfterDecimalSeparator,
  caretForDigitIndex,
  digitsBeforeCaret,
  formatDecimalInput,
  formatIntegerDigits,
  formatStoredDecimal,
  formatStoredInteger,
  integerDigitsOnly,
  parseDecimalInput,
  parseIntegerInput,
} from '@/lib/number-format';

type NumberInputProps = {
  readonly id?: string;
  /** Clean numeric quantity, or `null` for an empty field. Never `0` for "no value". */
  readonly value: number | null;
  /** Unit rendered as a right-side adornment; empty string renders no suffix. */
  readonly suffix?: string;
  /** `0` for whole-number counts, `>0` for decimal areas/cubature. Defaults to `0`. */
  readonly decimals?: number;
  /**
   * Whether to apply de-DE thousands grouping (integer mode only). Defaults to
   * `true`. Set `false` for ordinal values such as years, which read as a label
   * and must render bare (`1904`, not `1.904`).
   */
  readonly grouping?: boolean;
  readonly placeholder?: string;
  readonly onChange: (value: number | null) => void;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly 'aria-label'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
};

/**
 * Unit-suffixed numeric quantity input that masks the value with `de-DE`
 * thousands grouping as the user types. Configurable for an integer (count) or
 * decimal (area/cubature) mode and a unit suffix. The mask is presentation
 * only: `onChange` always emits a clean numeric value (or `null` when empty),
 * never the formatted string or the unit, and the value is always
 * non-negative. Visual parity with `SuffixInput`; shares the locale and caret
 * core with `CurrencyInput` via `@/lib/number-format`.
 */
export function NumberInput(props: NumberInputProps) {
  const {
    id,
    value,
    suffix = '',
    decimals = 0,
    grouping = true,
    placeholder,
    onChange,
    disabled = false,
    invalid = false,
  } = props;
  const ariaDescribedBy = props['aria-describedby'];
  const ariaLabel = props['aria-label'];

  const isDecimal = decimals > 0;

  const inputRef = useRef<HTMLInputElement>(null);
  const caretDigitIndex = useRef<number | null>(null);
  const caretAfterSeparator = useRef(false);

  const formatStored = (next: number | null) =>
    isDecimal ? formatStoredDecimal(next, decimals) : formatStoredInteger(next, grouping);

  const [display, setDisplay] = useState(() => formatStored(value));
  const [lastEmitted, setLastEmitted] = useState<number | null>(value);

  // Re-sync the display when the controlled value changes from the outside
  // (mount with a stored value, form reset) — but not when it merely echoes
  // back what this input just emitted, which would clobber the caret.
  if (value !== lastEmitted) {
    setLastEmitted(value);
    setDisplay(formatStored(value));
  }

  useLayoutEffect(() => {
    const target = caretDigitIndex.current;

    if (target === null || inputRef.current === null) return;

    const caret = caretAfterSeparator.current
      ? caretAfterDecimalSeparator(display, target)
      : caretForDigitIndex(display, target);
    inputRef.current.setSelectionRange(caret, caret);
    caretDigitIndex.current = null;
    caretAfterSeparator.current = false;
  }, [display]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const selectionStart = event.target.selectionStart ?? raw.length;

    caretDigitIndex.current = digitsBeforeCaret(raw, selectionStart);

    if (isDecimal) {
      caretAfterSeparator.current = raw[selectionStart - 1] === ',';

      setDisplay(formatDecimalInput(raw, decimals));

      const next = parseDecimalInput(raw, decimals);
      setLastEmitted(next);
      onChange(next);

      return;
    }

    const digits = integerDigitsOnly(raw);
    setDisplay(formatIntegerDigits(digits, grouping));

    const next = parseIntegerInput(raw);
    setLastEmitted(next);
    onChange(next);
  };

  const wrapperClasses = [
    'flex h-control-md w-full items-stretch overflow-hidden rounded-md border bg-surface',
    'transition-colors duration-micro ease-out',
    'focus-within:border-interactive focus-within:shadow-focus',
    invalid ? 'border-danger-500 focus-within:border-danger-500 focus-within:shadow-focus-danger' : 'border-border',
    disabled ? 'opacity-60' : '',
  ]
    .filter((c) => c !== '')
    .join(' ');

  return (
    <div className={wrapperClasses}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={display}
        placeholder={placeholder}
        onChange={handleChange}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        inputMode={isDecimal ? 'decimal' : 'numeric'}
        className="min-w-0 flex-1 bg-transparent px-3 py-2 text-base font-sans text-text-primary focus:outline-none disabled:cursor-not-allowed"
      />
      {suffix !== '' && (
        <span
          aria-hidden
          className="inline-flex items-center border-l border-border bg-neutral-50 px-3 text-base-sm text-text-tertiary"
        >
          {suffix}
        </span>
      )}
    </div>
  );
}
